import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Package, Search, Settings, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/platform`;

export default function SuperAdminModuleHub() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const token = localStorage.getItem('superAdminToken');
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('superAdminToken')}` });

  useEffect(() => {
    axios.get(`${API}/tenants`, { headers: getHeaders() })
      .then(r => setTenants(r.data))
      .catch(() => toast.error('Failed to load tenants'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = tenants.filter(t =>
    t.name?.toLowerCase().includes(search.toLowerCase()) ||
    t.slug?.toLowerCase().includes(search.toLowerCase())
  );

  const planColors = {
    free: 'bg-slate-700 text-slate-300',
    starter: 'bg-blue-600/20 text-blue-400',
    pro: 'bg-violet-600/20 text-violet-400',
    enterprise: 'bg-amber-600/20 text-amber-400',
  };

  return (
    <div data-testid="module-hub-page" className="space-y-6">
      <div>
        <h1 className="text-2xl font-extrabold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Module Access Control
        </h1>
        <p className="text-sm text-slate-400">
          Configure which modules and submodules each tenant can access. Select a tenant to customize.
        </p>
      </div>

      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <Input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search tenants..."
          className="pl-9 bg-slate-800 border-slate-700 text-white"
          data-testid="module-hub-search"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600" />
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(tenant => (
            <div
              key={tenant.id}
              data-testid={`module-tenant-${tenant.slug}`}
              className="flex items-center justify-between p-4 bg-slate-800/30 border border-slate-700 rounded-lg hover:bg-slate-800/60 transition-colors cursor-pointer group"
              onClick={() => navigate(`/super-admin/tenants/${tenant.slug}/modules`)}
            >
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center shrink-0">
                  <Package size={18} className="text-violet-400" />
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-white truncate">{tenant.name}</p>
                  <p className="text-xs text-slate-500">{tenant.slug} &middot; {tenant.owner_email || ''}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-xs font-bold px-2 py-1 rounded uppercase ${planColors[tenant.plan] || planColors.free}`}>
                  {tenant.plan || 'free'}
                </span>
                <span className="text-xs text-slate-500">{tenant.status}</span>
                <Button size="sm" variant="outline" className="border-violet-500/30 text-violet-400 hover:bg-violet-600/20 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Settings size={14} className="mr-1" /> Configure
                </Button>
                <ChevronRight size={16} className="text-slate-600 group-hover:text-violet-400 transition-colors" />
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <p className="text-center text-slate-500 py-8">No tenants found</p>
          )}
        </div>
      )}
    </div>
  );
}
